import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  testsTaken: number;
  testsLimit: number;
  limitReached?: boolean; // Nuevo prop para distinguir el contexto
}

export default function UpgradeModal({
  isOpen,
  onClose,
  testsTaken,
  testsLimit,
  limitReached = false, // Por defecto es false (modal promocional)
}: UpgradeModalProps) {
  const { t } = useTranslation(['app', 'common']);
  const { containerRef } = useFocusTrap<HTMLDivElement>({
    isActive: isOpen,
    onEscape: onClose,
    closeOnEscape: true,
  });

  if (!isOpen) return null;

  const handleViewPricing = () => {
    window.location.href = '/pricing';
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 text-white p-6 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            aria-label={t('common:close')}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          {limitReached ? (
            <>
              <h2 id="upgrade-modal-title" className="text-3xl font-bold mb-2">{t('app:upgradeModal.limitReachedTitle')}</h2>
              <p className="text-lg opacity-95 mb-3">
                {t('app:upgradeModal.usedTestsOf', { used: testsTaken, limit: testsLimit })}
              </p>
              <p className="text-xl font-semibold opacity-95">
                {t('app:upgradeModal.continueWithPro')}
              </p>
            </>
          ) : (
            <>
              <h2 id="upgrade-modal-title" className="text-3xl font-bold mb-2">{t('app:upgradeModal.unlockTitle')}</h2>
              <p className="text-xl font-semibold opacity-95">
                {t('app:upgradeModal.continueWithPro')}
              </p>
            </>
          )}
        </div>

        {/* Content */}
        <div className="p-6">

          {/* Features comparison */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Free Plan */}
            <div className="border-2 border-gray-300 rounded-xl p-4 bg-gray-50">
              <div className="text-center mb-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                  {t('app:upgradeModal.currentPlan')}
                </p>
                <p className="text-3xl font-extrabold text-gray-900 mb-1">Free</p>
                <p className="text-xl text-gray-600">{t('app:upgradeModal.freePrice')}</p>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">
                    <span className="font-semibold">{t('app:upgradeModal.testsPerMonth', { count: testsLimit })}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">{t('app:upgradeModal.basicAiAnalysis')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">{t('app:upgradeModal.testsAvailable')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="text-gray-400 line-through">{t('app:upgradeModal.unlimitedTests')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="text-gray-400 line-through">{t('app:upgradeModal.advancedReports')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="text-gray-400 line-through">{t('app:upgradeModal.prioritySupport')}</span>
                </li>
              </ul>
            </div>

            {/* Pro Plan */}
            <div className="border-4 border-purple-500 rounded-xl p-4 bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-50 relative shadow-xl">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                {t('app:upgradeModal.recommended')}
              </div>
              <div className="text-center mb-4 mt-2">
                <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-1">
                  {t('app:upgradeModal.proPlan')}
                </p>
                <p className="text-3xl font-extrabold text-gray-900 mb-1">Pro</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-extrabold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                    €19.90
                  </span>
                  <span className="text-lg text-gray-600">{t('app:upgradeModal.perMonth')}</span>
                </div>
              </div>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="font-bold text-gray-900">{t('app:upgradeModal.proUnlimitedTests')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-800">{t('app:upgradeModal.advancedAiAnalysis')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-800">{t('app:upgradeModal.thirtyPlusTests')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-800">{t('app:upgradeModal.customPdfReports')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-800">{t('app:upgradeModal.exportToExcel')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-800">{t('app:upgradeModal.prioritySupport247')}</span>
                </li>
              </ul>
              <button
                onClick={handleViewPricing}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-bold text-base hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {t('app:upgradeModal.upgradeToPro')}
              </button>
              <p className="text-center text-gray-600 text-xs mt-2">
                {t('app:upgradeModal.cancelAnytime')}
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center space-y-2">
            <button
              onClick={handleViewPricing}
              className="text-purple-600 hover:text-purple-800 font-semibold underline text-base"
            >
              Ver planes y precios completos →
            </button>
            <div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-xs underline"
              >
                Continuar con plan gratuito (reinicia el 1º del mes)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
